import { reduce } from 'lodash-es';

import { services } from 'services/base';
import {
  AuthManagerInterface,
  AuthMode,
  CreateAuthManagerService,
  DestroyAuthManagerService,
  GetAccessTokenAsyncService,
  GetAccessTokenService,
  GetAuthManagerService,
  GetAuthModeService,
  GetLogoutMessageService,
  GetProfileService,
  GetUserService,
  LoginService,
  LogoutService,
  OnUserChangeService,
  SilentLogoutService,
} from 'types';
import { getMainConfig, logger, registerStateServices } from 'utils';

import AuthManager, { ACTION_ROOT_USER_CHANGE } from './AuthManager';
import { getMockUser } from './helpers';

const SERVICE_PREFIX = 'appFrame.auth';

type AuthState = {
  sessionIsExpired: boolean;
  rootClient?: AuthManagerInterface;
  clients: Record<string, AuthManagerInterface>;
};

const initAuthServices = () => {
  // security services definition must be work on module level
  const { clearAction, registerAction, registerService } = services;

  const state: AuthState = {
    sessionIsExpired: false,
    rootClient: undefined,
    clients: {},
  };

  const logout = async (silent: boolean) => {
    await reduce(
      state.clients,
      async (initial, client, moduleId) => {
        await initial;
        try {
          await services.appFrame.auth.destroyAuthManager(moduleId);
        } catch (err) {
          logger.error(`Unsuccessful logout from ${moduleId} auth manager!`, err);
        }
      },
      Promise.resolve(),
    );

    try {
      await services.appFrame.auth.destroyAuthManager(null, silent);
    } catch (err) {
      logger.error(`Unsuccessful logout from root auth manager!`, err);
    }
  };

  registerService<GetAccessTokenService>(
    `${SERVICE_PREFIX}.getAccessToken`,
    (moduleId) => services.appFrame.auth.getAuthManager(moduleId)?.getAccessToken() ?? null,
  );

  registerService<GetAccessTokenAsyncService>(
    `${SERVICE_PREFIX}.getAccessTokenAsync`,
    (moduleId) => services.appFrame.auth.getAuthManager(moduleId)?.getAccessTokenAsync() ?? null,
  );

  registerService<GetProfileService>(
    `${SERVICE_PREFIX}.getProfile`,
    (moduleId) => services.appFrame.auth.getAuthManager(moduleId)?.getProfile() || null,
  );

  registerService<GetUserService>(
    `${SERVICE_PREFIX}.getUser`,
    (moduleId) => services.appFrame.auth.getAuthManager(moduleId)?.getUser() || null,
  );

  registerService<LoginService>(`${SERVICE_PREFIX}.login`, () => {
    services.appFrame.auth.getAuthManager()?.login();
  });

  registerService<LogoutService>(`${SERVICE_PREFIX}.logout`, () => logout(false));

  registerService<SilentLogoutService>(`${SERVICE_PREFIX}.silentLogout`, () => logout(true));

  registerService<GetLogoutMessageService>(`${SERVICE_PREFIX}.getLogoutMessage`, () => null);

  registerStateServices({
    state,
    stateKey: 'sessionIsExpired',
    servicePrefix: SERVICE_PREFIX,
    initValue: false,
    serviceMethods: {
      get: 'isSessionExpired',
      set: 'setSessionExpired',
      onChange: 'onSessionExpired',
    },
  });

  registerService<GetAuthModeService>(`${SERVICE_PREFIX}.getAuthMode`, () => {
    const oidcConfig = getMainConfig('oidc');

    const mockUser = getMockUser(oidcConfig);
    if (mockUser) {
      return AuthMode.LoggedIn;
    }

    if (!oidcConfig) {
      return AuthMode.NoLogin;
    }

    if (oidcConfig.allowAutoLogin === false) {
      return AuthMode.Anonymous;
    }

    return AuthMode.LoggedIn;
  });

  registerService<CreateAuthManagerService>(`${SERVICE_PREFIX}.createAuthManager`, (oidcConfig, moduleId) => {
    const authMode = services.appFrame.auth.getAuthMode();
    if (authMode === AuthMode.NoLogin || !oidcConfig) {
      return null;
    }

    if (services.appFrame.auth.getAuthManager(moduleId)) {
      throw new Error(`Auth manager already exists: ${moduleId || 'root'}!`);
    }

    const client: AuthManagerInterface = new AuthManager(oidcConfig, moduleId);
    if (moduleId) {
      state.clients[moduleId] = client;
    } else {
      state.rootClient = client;
    }

    return client;
  });

  registerService<DestroyAuthManagerService>(`${SERVICE_PREFIX}.destroyAuthManager`, (moduleId, silentLogout) => {
    const client = moduleId ? state.clients[moduleId] : state.rootClient;
    if (!client) {
      return;
    }

    if (moduleId) {
      delete state.clients[moduleId];
    } else {
      delete state.rootClient;
    }

    client.destroy(silentLogout);
  });

  registerService<GetAuthManagerService>(
    `${SERVICE_PREFIX}.getAuthManager`,
    (moduleId) => (moduleId ? state.clients[moduleId] : state.rootClient) || null,
  );

  // create a subsciption which listens to root user changes (or when no root client the cleanup fn is a `noop`)
  registerService<OnUserChangeService>(`${SERVICE_PREFIX}.onUserChange`, (callback) => {
    // NOTE: we are using the sideeffect that root auth manager is never destroyed during the app life cycle
    // when it is destroyed the action subscription is automatically removed, and a re-registration is needed
    registerAction(ACTION_ROOT_USER_CHANGE, callback, { order: 1 });
    return () => clearAction(ACTION_ROOT_USER_CHANGE, callback);
  });
};

export default initAuthServices;
